import React, { useCallback, useId, useState } from 'react';
import { Badge, IconButton, Tooltip } from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import useNotifications from '../../Hooks/useNotifications';
import useTailwindBrand from '../../Theme/useTailwindBrand';
import NotificationPanel from './NotificationPanel';

interface NotificationBellProps {
  /** AppBar sur fond sombre : icône claire. */
  isDark?: boolean;
  /** Barre mobile : bouton plus compact. */
  size?: 'small' | 'medium';
}

/**
 * Cloche de l'AppBar : badge des nouvelles notifications (non lues arrivées depuis la
 * dernière ouverture du panneau) et panneau des notifications. L'état vient du store
 * partagé de useNotifications (une seule requête pour les cloches desktop et mobile),
 * la cloche le transmet à son panneau. Ouvrir le panneau enregistre le repère « vu » :
 * le badge retombe à zéro, les notifications restent non lues.
 */
const NotificationBell: React.FC<NotificationBellProps> = ({ isDark = false, size = 'medium' }) => {
  const { brandConfig } = useTailwindBrand();
  const notifications = useNotifications();
  const { markSeen } = notifications;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const panelId = useId();

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    markSeen();
  }, [markSeen]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // personne de connecté (ou session en cours de restauration) : pas de cloche
  if (!notifications.enabled) return null;

  const { newCount } = notifications;
  const open = Boolean(anchorEl);
  const ariaLabel = newCount > 0
    ? `Notifications, ${newCount} nouvelle${newCount > 1 ? 's' : ''}`
    : 'Notifications';

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton
          size={size}
          onClick={handleOpen}
          aria-label={ariaLabel}
          aria-haspopup="true"
          aria-controls={open ? panelId : undefined}
          aria-expanded={open ? 'true' : undefined}
          sx={{
            // maquettes « Déclarer un cours » : carré arrondi bordé, badge rouge en haut à droite
            width: size === 'small' ? 36 : 40,
            height: size === 'small' ? 36 : 40,
            borderRadius: '12px',
            border: isDark ? '1px solid rgba(255,255,255,0.25)' : '1px solid #DCE3EC',
            backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : '#fff',
            color: isDark ? 'rgba(255,255,255,0.9)' : '#1B2A41',
            '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.16)' : '#F6F8FB' },
          }}
        >
          <Badge
            badgeContent={newCount}
            max={99}
            overlap="circular"
            invisible={newCount === 0}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: brandConfig.colors.error,
                color: '#fff',
                fontWeight: 800,
                fontSize: '0.6875rem',
                height: 18,
                minWidth: 18,
                px: 0.5,
                border: isDark ? 'none' : '2px solid #fff',
                transform: 'scale(1) translate(45%, -45%)',
              },
            }}
          >
            <NotificationsNoneOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <NotificationPanel id={panelId} anchorEl={anchorEl} onClose={handleClose} notifications={notifications} />
    </>
  );
};

export default NotificationBell;
